<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';

import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from 'dashboard/routes/dashboard/settings/account/components/SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

const templates = ref([]);
const lastUpdatedAt = ref(null);
const isFetching = ref(false);
const isSyncing = ref(false);
const isSaving = ref(false);
const isUploadingAsset = ref(false);
const deleteTemplateName = ref('');

const defaultForm = () => ({
  templateId: '',
  isEditing: false,
  name: '',
  category: 'UTILITY',
  language: 'en_US',
  headerType: 'none',
  headerText: '',
  headerExamples: [],
  headerFile: null,
  uploadedAsset: null,
  body: '',
  bodyExamples: [],
  footer: '',
  buttons: [],
});

const form = reactive(defaultForm());

const headerTypes = [
  { label: t('WHATSAPP_TEMPLATES.MANAGER.HEADER_TYPES.NONE'), value: 'none' },
  { label: t('WHATSAPP_TEMPLATES.MANAGER.HEADER_TYPES.TEXT'), value: 'text' },
  { label: t('WHATSAPP_TEMPLATES.MANAGER.HEADER_TYPES.IMAGE'), value: 'image' },
  { label: t('WHATSAPP_TEMPLATES.MANAGER.HEADER_TYPES.VIDEO'), value: 'video' },
  {
    label: t('WHATSAPP_TEMPLATES.MANAGER.HEADER_TYPES.DOCUMENT'),
    value: 'document',
  },
];

const categories = ['UTILITY', 'MARKETING'];
const languages = [
  { label: 'English (US)', value: 'en_US' },
  { label: 'English (UK)', value: 'en_GB' },
  { label: 'Spanish (Spain)', value: 'es_ES' },
  { label: 'Spanish (Mexico)', value: 'es_MX' },
  { label: 'Spanish (Argentina)', value: 'es_AR' },
  { label: 'Portuguese (Brazil)', value: 'pt_BR' },
  { label: 'Portuguese (Portugal)', value: 'pt_PT' },
  { label: 'French', value: 'fr' },
  { label: 'German', value: 'de' },
  { label: 'Italian', value: 'it' },
];
const buttonTypes = [
  {
    label: t('WHATSAPP_TEMPLATES.MANAGER.BUTTON_TYPES.QUICK_REPLY'),
    value: 'quick_reply',
  },
  { label: t('WHATSAPP_TEMPLATES.MANAGER.BUTTON_TYPES.URL'), value: 'url' },
  {
    label: t('WHATSAPP_TEMPLATES.MANAGER.BUTTON_TYPES.PHONE_NUMBER'),
    value: 'phone_number',
  },
];

const isMultimediaHeader = computed(() =>
  ['image', 'video', 'document'].includes(form.headerType)
);

const variableIndexes = text =>
  [...new Set((text || '').match(/\{\{\d+\}\}/g) || [])]
    .map(variable => Number(variable.replace(/\D/g, '')))
    .sort((first, second) => first - second);

const acceptedFileTypes = computed(() => {
  if (form.headerType === 'image') return 'image/*';
  if (form.headerType === 'video') return 'video/*';
  if (form.headerType === 'document') {
    return [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
    ].join(',');
  }
  return '';
});

const bodyVariableIndexes = computed(() => variableIndexes(form.body));
const headerVariableIndexes = computed(() => variableIndexes(form.headerText));

const canSubmit = computed(() => {
  if (!form.body.trim() || !form.category || !form.language) return false;
  if (!form.isEditing && !form.name.trim()) return false;
  if (form.headerType === 'text' && !form.headerText.trim()) return false;
  if (isMultimediaHeader.value && !form.headerFile && !form.uploadedAsset) {
    return false;
  }
  return true;
});

const syncExampleArray = (target, indexes) => {
  if (target.length > indexes.length) {
    target.splice(indexes.length);
  }

  indexes.forEach((_, index) => {
    if (!target[index]) target[index] = `sample_${index + 1}`;
  });
};

watch(bodyVariableIndexes, indexes =>
  syncExampleArray(form.bodyExamples, indexes)
);
watch(headerVariableIndexes, indexes =>
  syncExampleArray(form.headerExamples, indexes)
);

const resetForm = () => {
  Object.assign(form, defaultForm());
};

const getTemplateComponent = (template, type) =>
  template.components?.find(component => component.type === type);

const templateBody = template =>
  getTemplateComponent(template, 'BODY')?.text || '';

const templateHeader = template => {
  const header = getTemplateComponent(template, 'HEADER');
  return header?.format || t('WHATSAPP_TEMPLATES.MANAGER.HEADER_TYPES.NONE');
};

const templateMeta = template =>
  [
    t(`WHATSAPP_TEMPLATES.MANAGER.CATEGORIES.${template.category}`) || template.category,
    template.language,
    templateHeader(template),
  ]
    .filter(Boolean)
    .join(' | ');

const fieldClass =
  'w-full rounded-xl border border-n-weak bg-n-alpha-2 px-4 py-3 text-sm text-n-slate-12 shadow-sm outline-none transition focus:border-n-brand focus:bg-n-solid-1 focus:ring-2 focus:ring-n-brand/10 disabled:cursor-not-allowed disabled:opacity-60';
const textareaClass = `${fieldClass} min-h-[7rem] resize-y leading-6`;
const fileFieldClass =
  'w-full rounded-xl border border-dashed border-n-weak bg-n-alpha-2 px-4 py-3 text-sm text-n-slate-11 transition hover:border-n-slate-6 hover:bg-n-alpha-3 file:mr-3 file:rounded-lg file:border-0 file:bg-n-brand file:px-3 file:py-2 file:text-sm file:font-medium file:text-white';

const fetchTemplates = async () => {
  try {
    isFetching.value = true;
    const response = await InboxesAPI.getWhatsappTemplates(props.inbox.id);
    templates.value = response.data.templates || [];
    lastUpdatedAt.value = response.data.last_updated_at;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATES.MANAGER.API.FETCH_ERROR')
    );
  } finally {
    isFetching.value = false;
  }
};

const syncTemplates = async () => {
  try {
    isSyncing.value = true;
    const response = await InboxesAPI.syncWhatsappTemplates(props.inbox.id);
    templates.value = response.data.templates || [];
    lastUpdatedAt.value = response.data.last_updated_at;
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.API.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATES.MANAGER.API.SYNC_ERROR')
    );
  } finally {
    isSyncing.value = false;
  }
};

const uploadTemplateAsset = async () => {
  if (!form.headerFile) return form.uploadedAsset;

  const formData = new FormData();
  formData.append('attachment', form.headerFile);

  if (form.uploadedAsset?.asset_id) {
    formData.append('previous_asset_id', form.uploadedAsset.asset_id);
  }

  isUploadingAsset.value = true;
  const response = await InboxesAPI.uploadTemplateAsset(
    props.inbox.id,
    formData
  );
  form.uploadedAsset = response.data;
  form.headerFile = null;
  return form.uploadedAsset;
};

const templatePayload = asset => ({
  template: {
    name: form.name,
    category: form.category,
    language: form.language,
    header_type: form.headerType,
    header_text: form.headerText,
    header_examples: form.headerExamples,
    asset_id: asset?.asset_id,
    body: form.body,
    body_examples: form.bodyExamples,
    footer: form.footer,
    buttons: form.buttons
      .filter(button => button.type && button.text)
      .map(button => ({
        type: button.type,
        text: button.text,
        url: button.url,
        phone_number: button.phoneNumber,
        examples: button.examples || [],
      })),
  },
});

const saveTemplate = async () => {
  if (!canSubmit.value) return;

  try {
    isSaving.value = true;
    const asset = isMultimediaHeader.value ? await uploadTemplateAsset() : null;
    const payload = templatePayload(asset);

    if (form.isEditing) {
      await InboxesAPI.updateWhatsappTemplate(
        props.inbox.id,
        form.templateId,
        payload
      );
      useAlert(t('WHATSAPP_TEMPLATES.MANAGER.API.UPDATE_SUCCESS'));
    } else {
      await InboxesAPI.createWhatsappTemplate(props.inbox.id, payload);
      useAlert(t('WHATSAPP_TEMPLATES.MANAGER.API.CREATE_SUCCESS'));
    }

    resetForm();
    await fetchTemplates();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATES.MANAGER.API.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
    isUploadingAsset.value = false;
  }
};

const editTemplate = template => {
  resetForm();

  const header = getTemplateComponent(template, 'HEADER') || {};
  const body = getTemplateComponent(template, 'BODY') || {};
  const footer = getTemplateComponent(template, 'FOOTER') || {};
  const buttons = getTemplateComponent(template, 'BUTTONS')?.buttons || [];

  form.templateId = template.id;
  form.isEditing = true;
  form.name = template.name;
  form.category = template.category || 'UTILITY';
  form.language = template.language || 'en_US';
  form.headerType = (header.format || 'none').toLowerCase();
  form.headerText = header.text || '';
  form.body = body.text || '';
  form.footer = footer.text || '';
  form.buttons = buttons.map(button => ({
    type: button.type?.toLowerCase(),
    text: button.text || '',
    url: button.url || '',
    phoneNumber: button.phone_number || '',
    examples: Array.isArray(button.example) ? button.example : [],
  }));
};

const addButton = () => {
  if (form.buttons.length >= 3) return;

  form.buttons.push({
    type: 'quick_reply',
    text: '',
    url: '',
    phoneNumber: '',
    examples: [],
  });
};

const removeButton = index => {
  form.buttons.splice(index, 1);
};

const onHeaderTypeChange = value => {
  form.headerType = value;
  form.headerText = value === 'text' ? form.headerText : '';
  form.headerFile = null;
  form.uploadedAsset = null;
};

const isAcceptedHeaderFile = file => {
  if (form.headerType === 'image') return file.type.startsWith('image/');
  if (form.headerType === 'video') return file.type.startsWith('video/');
  if (form.headerType === 'document') {
    return acceptedFileTypes.value.split(',').includes(file.type);
  }
  return false;
};

const onHeaderFileChange = event => {
  const [file] = event.target.files || [];
  if (file && !isAcceptedHeaderFile(file)) {
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.API.INVALID_FILE'));
    event.target.value = '';
    form.headerFile = null;
    return;
  }

  form.headerFile = file || null;
  form.uploadedAsset = file ? null : form.uploadedAsset;
};

const confirmDelete = templateName => {
  deleteTemplateName.value = templateName;
};

const deleteTemplate = async templateName => {
  try {
    await InboxesAPI.deleteWhatsappTemplate(props.inbox.id, templateName);
    deleteTemplateName.value = '';
    useAlert(t('WHATSAPP_TEMPLATES.MANAGER.API.DELETE_SUCCESS'));
    await fetchTemplates();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATES.MANAGER.API.DELETE_ERROR')
    );
  }
};

const normalizedStatus = status => status || 'UNKNOWN';

onMounted(fetchTemplates);
</script>

<template>
  <div class="mx-8">
    <SectionLayout
      :title="$t('WHATSAPP_TEMPLATES.MANAGER.TITLE')"
      :description="$t('WHATSAPP_TEMPLATES.MANAGER.DESCRIPTION')"
    >
      <template #headerActions>
        <div class="flex justify-end gap-2">
          <NextButton
            faded
            slate
            :label="$t('WHATSAPP_TEMPLATES.MANAGER.REFRESH')"
            :is-loading="isFetching"
            @click="fetchTemplates"
          />
          <NextButton
            :label="$t('WHATSAPP_TEMPLATES.MANAGER.SYNC')"
            :is-loading="isSyncing"
            @click="syncTemplates"
          />
        </div>
      </template>

      <div class="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div class="grid gap-3">
          <div v-if="lastUpdatedAt" class="text-sm text-n-slate-11">
            {{
              $t('WHATSAPP_TEMPLATES.MANAGER.LAST_SYNCED', {
                date: lastUpdatedAt,
              })
            }}
          </div>

          <div
            v-if="!templates.length && !isFetching"
            class="rounded-xl border border-n-weak bg-n-alpha-2 p-6 text-sm text-n-slate-11"
          >
            {{ $t('WHATSAPP_TEMPLATES.MANAGER.EMPTY') }}
          </div>

          <div
            v-for="template in templates"
            :key="`${template.name}-${template.language}`"
            class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div class="flex flex-wrap items-center gap-2">
                  <h5 class="text-base font-semibold text-n-slate-12">
                    {{ template.name }}
                  </h5>
                  <span
                    class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium uppercase text-n-slate-11"
                  >
                    {{ normalizedStatus(template.status) }}
                  </span>
                </div>
                <p class="mt-1 text-xs uppercase tracking-wide text-n-slate-10">
                  {{ templateMeta(template) }}
                </p>
              </div>
              <div class="flex flex-wrap gap-2">
                <NextButton
                  xs
                  faded
                  slate
                  :label="$t('WHATSAPP_TEMPLATES.MANAGER.EDIT')"
                  @click="editTemplate(template)"
                />
                <NextButton
                  v-if="deleteTemplateName !== template.name"
                  xs
                  faded
                  ruby
                  :label="$t('WHATSAPP_TEMPLATES.MANAGER.DELETE')"
                  @click="confirmDelete(template.name)"
                />
                <template v-else>
                  <NextButton
                    xs
                    ruby
                    :label="$t('WHATSAPP_TEMPLATES.MANAGER.CONFIRM_DELETE')"
                    @click="deleteTemplate(template.name)"
                  />
                  <NextButton
                    xs
                    ghost
                    slate
                    :label="$t('WHATSAPP_TEMPLATES.MANAGER.CANCEL')"
                    @click="deleteTemplateName = ''"
                  />
                </template>
              </div>
            </div>
            <p class="line-clamp-3 text-sm text-n-slate-11">
              {{ templateBody(template) }}
            </p>
          </div>
        </div>

        <form class="grid content-start gap-4" @submit.prevent="saveTemplate">
          <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
            <div class="mb-4 flex items-center justify-between gap-3">
              <h5 class="text-base font-semibold text-n-slate-12">
                {{
                  form.isEditing
                    ? $t('WHATSAPP_TEMPLATES.MANAGER.EDIT_TITLE')
                    : $t('WHATSAPP_TEMPLATES.MANAGER.CREATE_TITLE')
                }}
              </h5>
              <NextButton
                v-if="form.isEditing"
                xs
                ghost
                slate
                type="button"
                :label="$t('WHATSAPP_TEMPLATES.MANAGER.CANCEL')"
                @click="resetForm"
              />
            </div>

            <div class="grid gap-4">
              <WithLabel
                :label="$t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.NAME')"
                name="template_name"
              >
                <input
                  v-model="form.name"
                  :class="fieldClass"
                  :disabled="form.isEditing"
                  :placeholder="
                    $t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.NAME_PLACEHOLDER')
                  "
                />
              </WithLabel>

              <div class="grid grid-cols-2 gap-3">
                <WithLabel
                  :label="$t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.CATEGORY')"
                  name="category"
                >
                  <select v-model="form.category" :class="fieldClass">
                    <option
                      v-for="category in categories"
                      :key="category"
                      :value="category"
                    >
                      {{ $t(`WHATSAPP_TEMPLATES.MANAGER.CATEGORIES.${category}`) }}
                    </option>
                  </select>
                </WithLabel>

                <WithLabel
                  :label="$t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.LANGUAGE')"
                  name="language"
                >
                  <select v-model="form.language" :class="fieldClass">
                    <option
                      v-for="language in languages"
                      :key="language.value"
                      :value="language.value"
                    >
                      {{ language.label }}
                    </option>
                  </select>
                </WithLabel>
              </div>

              <WithLabel
                :label="$t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.HEADER_TYPE')"
                name="header_type"
              >
                <select
                  :value="form.headerType"
                  :class="fieldClass"
                  @change="onHeaderTypeChange($event.target.value)"
                >
                  <option
                    v-for="headerType in headerTypes"
                    :key="headerType.value"
                    :value="headerType.value"
                  >
                    {{ headerType.label }}
                  </option>
                </select>
              </WithLabel>

              <WithLabel
                v-if="form.headerType === 'text'"
                :label="$t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.HEADER_TEXT')"
                name="header_text"
              >
                <input v-model="form.headerText" :class="fieldClass" />
              </WithLabel>

              <div v-if="headerVariableIndexes.length" class="grid gap-2">
                <label
                  v-for="(variable, index) in headerVariableIndexes"
                  :key="variable"
                  class="grid gap-1 text-sm text-n-slate-11"
                >
                  {{
                    $t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.HEADER_EXAMPLE', {
                      variable,
                    })
                  }}
                  <input
                    v-model="form.headerExamples[index]"
                    :class="fieldClass"
                  />
                </label>
              </div>

              <WithLabel
                v-if="isMultimediaHeader"
                :label="$t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.HEADER_FILE')"
                name="header_file"
              >
                <div class="grid gap-2">
                  <input
                    type="file"
                    :class="fileFieldClass"
                    :accept="acceptedFileTypes"
                    @change="onHeaderFileChange"
                  />
                  <p class="text-sm text-n-slate-11">
                    {{
                      form.headerFile?.name ||
                      form.uploadedAsset?.file_name ||
                      $t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.HEADER_FILE_HINT')
                    }}
                  </p>
                </div>
              </WithLabel>

              <WithLabel
                :label="$t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.BODY')"
                name="body"
              >
                <textarea
                  v-model="form.body"
                  :class="textareaClass"
                  rows="5"
                  :placeholder="
                    $t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.BODY_PLACEHOLDER')
                  "
                />
              </WithLabel>

              <div v-if="bodyVariableIndexes.length" class="grid gap-2">
                <label
                  v-for="(variable, index) in bodyVariableIndexes"
                  :key="variable"
                  class="grid gap-1 text-sm text-n-slate-11"
                >
                  {{
                    $t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.BODY_EXAMPLE', {
                      variable,
                    })
                  }}
                  <input
                    v-model="form.bodyExamples[index]"
                    :class="fieldClass"
                  />
                </label>
              </div>

              <WithLabel
                :label="$t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.FOOTER')"
                name="footer"
              >
                <input v-model="form.footer" :class="fieldClass" />
              </WithLabel>

              <div class="grid gap-3">
                <div class="flex items-center justify-between gap-3">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ $t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.BUTTONS') }}
                  </label>
                  <NextButton
                    xs
                    faded
                    slate
                    type="button"
                    :disabled="form.buttons.length >= 3"
                    :label="$t('WHATSAPP_TEMPLATES.MANAGER.ADD_BUTTON')"
                    @click="addButton"
                  />
                </div>

                <div
                  v-for="(button, index) in form.buttons"
                  :key="index"
                  class="grid gap-2 rounded-lg border border-n-weak p-3"
                >
                  <div class="flex items-center justify-between gap-2">
                    <select
                      v-model="button.type"
                      :class="`${fieldClass} mb-0 flex-1`"
                    >
                      <option
                        v-for="buttonType in buttonTypes"
                        :key="buttonType.value"
                        :value="buttonType.value"
                      >
                        {{ buttonType.label }}
                      </option>
                    </select>
                    <NextButton
                      xs
                      ghost
                      ruby
                      type="button"
                      :label="$t('WHATSAPP_TEMPLATES.MANAGER.REMOVE')"
                      @click="removeButton(index)"
                    />
                  </div>
                  <input
                    v-model="button.text"
                    :class="fieldClass"
                    :placeholder="
                      $t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.BUTTON_TEXT')
                    "
                  />
                  <input
                    v-if="button.type === 'url'"
                    v-model="button.url"
                    :class="fieldClass"
                    :placeholder="
                      $t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.URL_PLACEHOLDER')
                    "
                  />
                  <input
                    v-if="button.type === 'phone_number'"
                    v-model="button.phoneNumber"
                    :class="fieldClass"
                    :placeholder="
                      $t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.PHONE_PLACEHOLDER')
                    "
                  />
                  <input
                    v-if="button.type === 'url' && button.url?.includes('{{')"
                    v-model="button.examples[0]"
                    :class="fieldClass"
                    :placeholder="
                      $t('WHATSAPP_TEMPLATES.MANAGER.FIELDS.URL_EXAMPLE')
                    "
                  />
                </div>
              </div>

              <NextButton
                type="submit"
                :disabled="!canSubmit"
                :label="
                  form.isEditing
                    ? $t('WHATSAPP_TEMPLATES.MANAGER.UPDATE')
                    : $t('WHATSAPP_TEMPLATES.MANAGER.CREATE')
                "
                :is-loading="isSaving || isUploadingAsset"
              />
            </div>
          </div>
        </form>
      </div>
    </SectionLayout>
  </div>
</template>
